import { Injectable, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import {
  ACCOUNT_REVIEW_LISTINGS_SEED,
  COMPETITOR_SETS_SEED,
  REVIEW_SOURCE_CONNECTIONS_SEED,
} from '@common/data';
import type {
  AccountReviewListing,
  Competitor,
  CompetitorSet,
  ReviewSourceConnection,
  ReviewSourceKey,
} from '@common/types';

const SAVE_LATENCY_MS = 300;
const SYNC_LATENCY_MS = 1100;

@Injectable({ providedIn: 'root' })
export class IntegrationsService {
  private readonly connections = signal<ReadonlyArray<ReviewSourceConnection>>(
    REVIEW_SOURCE_CONNECTIONS_SEED,
  );
  private readonly listings = signal<Record<string, AccountReviewListing[]>>(
    Object.fromEntries(
      Object.entries(ACCOUNT_REVIEW_LISTINGS_SEED).map(([id, rows]) => [id, rows.slice()]),
    ),
  );
  private readonly competitorSets = signal<Record<string, CompetitorSet>>({
    ...COMPETITOR_SETS_SEED,
  });

  readonly sourceConnections = this.connections.asReadonly();

  listingsFor(accountId: string): ReadonlyArray<AccountReviewListing> {
    return this.listings()[accountId] ?? [];
  }

  competitorSetFor(accountId: string): CompetitorSet {
    return (
      this.competitorSets()[accountId] ?? {
        accountId,
        market: '',
        benchmarkGroup: '',
        competitors: [],
      }
    );
  }

  syncNow(sourceKey: ReviewSourceKey): Observable<ReviewSourceConnection | undefined> {
    const now = new Date();
    this.connections.update((list) =>
      list.map((connection) =>
        connection.sourceKey === sourceKey
          ? {
              ...connection,
              status: 'connected',
              errorMessage: null,
              lastSyncIso: now.toISOString(),
              nextSyncIso: new Date(
                now.getTime() + connection.syncEveryHours * 60 * 60 * 1000,
              ).toISOString(),
            }
          : connection,
      ),
    );
    return of(this.connections().find((c) => c.sourceKey === sourceKey)).pipe(
      delay(SYNC_LATENCY_MS),
    );
  }

  setConnected(sourceKey: ReviewSourceKey, connected: boolean): Observable<void> {
    this.connections.update((list) =>
      list.map((connection) =>
        connection.sourceKey === sourceKey
          ? {
              ...connection,
              status: connected ? 'connected' : 'disconnected',
              errorMessage: null,
              lastSyncIso: connected ? new Date().toISOString() : null,
            }
          : connection,
      ),
    );
    return of(undefined).pipe(delay(SAVE_LATENCY_MS));
  }

  saveConnection(next: ReviewSourceConnection): Observable<ReviewSourceConnection> {
    this.connections.update((list) =>
      list.map((connection) => (connection.sourceKey === next.sourceKey ? next : connection)),
    );
    return of(next).pipe(delay(SAVE_LATENCY_MS));
  }

  saveListing(next: AccountReviewListing): Observable<AccountReviewListing> {
    this.listings.update((map) => ({
      ...map,
      [next.accountId]: (map[next.accountId] ?? []).map((listing) =>
        listing.sourceKey === next.sourceKey ? next : listing,
      ),
    }));
    return of(next).pipe(delay(SAVE_LATENCY_MS));
  }

  saveCompetitorSet(next: CompetitorSet): Observable<CompetitorSet> {
    this.competitorSets.update((map) => ({ ...map, [next.accountId]: next }));
    return of(next).pipe(delay(SAVE_LATENCY_MS));
  }

  addCompetitor(accountId: string, competitor: Omit<Competitor, 'id'>): Observable<CompetitorSet> {
    const current = this.competitorSetFor(accountId);
    const next: CompetitorSet = {
      ...current,
      accountId,
      competitors: [
        ...current.competitors,
        { ...competitor, id: `${accountId}-comp-${Date.now().toString(36)}` },
      ],
    };
    return this.saveCompetitorSet(next);
  }

  removeCompetitor(accountId: string, competitorId: string): Observable<CompetitorSet> {
    const current = this.competitorSetFor(accountId);
    return this.saveCompetitorSet({
      ...current,
      competitors: current.competitors.filter((competitor) => competitor.id !== competitorId),
    });
  }

  makePrimaryCompetitor(accountId: string, competitorId: string): Observable<CompetitorSet> {
    const current = this.competitorSetFor(accountId);
    return this.saveCompetitorSet({
      ...current,
      competitors: current.competitors.map((competitor) => ({
        ...competitor,
        isPrimary: competitor.id === competitorId,
      })),
    });
  }
}
